import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Sparkle,
  SignOut,
  CheckCircle,
  Circle,
  Clock,
  ChatCircleText,
  Lightning,
  Brain,
  ListNumbers,
  Eye,
  Confetti,
  CaretDown,
  Target,
  Books,
  Package,
  Trophy,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import HighlightsCard from "../components/HighlightsCard";

const ICONS = { Sparkle, Lightning, Brain, ListNumbers, Eye, Confetti };
const TONES = {
  yellow:  "bg-yellow-100 text-yellow-900",
  blue:    "bg-blue-100 text-blue-900",
  purple:  "bg-purple-100 text-purple-900",
  emerald: "bg-emerald-100 text-emerald-900",
  pink:    "bg-pink-100 text-pink-900",
  amber:   "bg-amber-100 text-amber-900",
};

function UpcomingWeekAccordion({ week }) {
  const [open, setOpen] = useState(false);
  const hasDetails =
    (week.tasks && week.tasks.length > 0) ||
    (week.sessions && week.sessions.length > 0) ||
    (week.materials && week.materials.length > 0) ||
    week.core_concept ||
    week.success_check ||
    (week.conversation_starters && week.conversation_starters.length > 0);

  return (
    <div
      className="rounded-xl bg-slate-50 border border-slate-100 overflow-hidden"
      data-testid={`parent-upcoming-week-${week.week_num}`}
    >
      <button
        type="button"
        onClick={() => hasDetails && setOpen((o) => !o)}
        disabled={!hasDetails}
        aria-expanded={open}
        data-testid={`parent-upcoming-toggle-${week.week_num}`}
        className={`w-full flex items-center justify-between gap-3 p-3 text-left ${
          hasDetails ? "hover:bg-slate-100 cursor-pointer" : "cursor-default"
        } transition`}
      >
        <div className="min-w-0">
          <div className="text-xs font-bold tracking-wider uppercase text-slate-500">
            Week {week.week_num} · {week.category}
          </div>
          <div className="font-outfit font-bold truncate">{week.title}</div>
          <div className="text-xs text-slate-500 truncate">{week.tagline}</div>
        </div>
        {hasDetails && (
          <CaretDown
            size={18}
            weight="bold"
            className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && hasDetails && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div
              className="px-4 pb-4 space-y-4 border-t border-slate-200/70 pt-4 bg-white"
              data-testid={`parent-upcoming-body-${week.week_num}`}
            >
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <Eye size={13} weight="bold" /> Preview only · read-only
              </div>

              {week.core_concept && (
                <p className="text-sm text-slate-700 leading-relaxed">{week.core_concept}</p>
              )}

              {week.tasks?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <Target size={13} weight="bold" /> What your child will do
                  </div>
                  <ul className="space-y-1.5">
                    {week.tasks.map((t, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-slate-800"
                        data-testid={`parent-upcoming-task-${week.week_num}-${i}`}
                      >
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {week.sessions?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <Books size={13} weight="bold" /> In class
                  </div>
                  <ol className="space-y-1.5">
                    {week.sessions.map((s, i) => (
                      <li key={i} className="text-sm flex gap-2">
                        <span className="font-bold tracking-wider uppercase text-slate-500 w-14 shrink-0 text-[11px] pt-0.5">
                          {s.time}
                        </span>
                        <span>
                          <span className="font-semibold text-slate-900">{s.title}</span>
                          <span className="text-slate-600"> — {s.description}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {week.materials?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <Package size={13} weight="bold" /> What to bring
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {week.materials.map((m, i) => (
                      <span
                        key={i}
                        className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {week.success_check && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                  <Trophy weight="fill" size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[11px] font-bold tracking-wider uppercase text-emerald-800">
                      Success check
                    </div>
                    <p className="text-sm text-slate-700">{week.success_check}</p>
                  </div>
                </div>
              )}

              {week.conversation_starters?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <ChatCircleText size={13} weight="bold" /> Ask at home
                  </div>
                  <ul className="space-y-1.5">
                    {week.conversation_starters.map((q, i) => (
                      <li
                        key={i}
                        className="font-caveat text-lg text-yellow-900 bg-yellow-100/70 rounded-xl px-3 py-2"
                      >
                        "{q}"
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [childIdx, setChildIdx] = useState(0);

  useEffect(() => {
    api.get("/parent/me").then(({ data }) => setData(data));
  }, []);

  if (!data)
    return (
      <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center text-slate-500" data-testid="parent-loading">
        Loading...
      </div>
    );

  const child = data.children[childIdx];

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <header className="sticky top-0 z-30 bg-[#FFFDF8]/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-yellow-400 flex items-center justify-center">
              <Sparkle weight="fill" className="text-yellow-950" size={20} />
            </div>
            <div>
              <div className="font-outfit font-black leading-none">Alpha AI Discovery Club</div>
              <div className="text-[11px] text-slate-500">Parent dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600" data-testid="parent-user-name">{user?.name}</span>
            <button
              onClick={logout}
              data-testid="parent-logout-btn"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
              title="Sign out"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {data.children.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center" data-testid="parent-no-children">
            <h2 className="font-outfit font-bold text-xl">No child linked yet</h2>
            <p className="text-slate-600 mt-2">Ask your teacher to link your child to this account.</p>
          </div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <p className="font-caveat text-2xl text-yellow-700">Welcome, {user?.name?.split(" ")[0]}</p>
              <h1 className="font-outfit text-3xl sm:text-4xl font-black tracking-tight" data-testid="parent-child-name">
                How {child.student.name} is doing
              </h1>
              <p className="text-slate-600 mt-1">{data.class_name}</p>
            </motion.div>

            {data.children.length > 1 && (
              <div className="flex gap-2 flex-wrap" data-testid="parent-child-switcher">
                {data.children.map((c, idx) => (
                  <button
                    key={c.student.id}
                    onClick={() => setChildIdx(idx)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold ${
                      idx === childIdx ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"
                    }`}
                  >
                    {c.student.name}
                  </button>
                ))}
              </div>
            )}

            {/* Progress summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-3xl p-6 border border-slate-200 md:col-span-2" data-testid="parent-progress-card">
                <div className="text-xs font-bold tracking-wider uppercase text-slate-500">Overall progress</div>
                <div className="font-outfit text-4xl font-black mt-1">
                  {child.total_tasks
                    ? Math.round(((child.verified_tasks ?? child.completed_tasks) / child.total_tasks) * 100)
                    : 0}
                  %
                </div>
                <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span data-testid="parent-verified-count">
                    {child.verified_tasks ?? child.completed_tasks} of {child.total_tasks} verified
                  </span>
                  {child.pending_tasks > 0 && (
                    <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid="parent-pending-count">
                      <Clock size={12} weight="fill" /> {child.pending_tasks} pending teacher review
                    </span>
                  )}
                </div>
                <div className="mt-4 h-3 bg-slate-100 rounded-full overflow-hidden flex">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${child.total_tasks ? ((child.verified_tasks ?? child.completed_tasks) / child.total_tasks) * 100 : 0}%` }}
                  />
                  {child.pending_tasks > 0 && (
                    <div
                      className="h-full bg-amber-300 transition-all"
                      style={{ width: `${child.total_tasks ? (child.pending_tasks / child.total_tasks) * 100 : 0}%` }}
                      title={`${child.pending_tasks} task(s) waiting for teacher review`}
                    />
                  )}
                </div>
              </div>
              <div className="bg-white rounded-3xl p-6 border border-slate-200">
                <div className="text-xs font-bold tracking-wider uppercase text-slate-500">Badges earned</div>
                <div className="font-outfit text-4xl font-black mt-1" data-testid="parent-badge-count">{child.badges.length}</div>
                <div className="text-sm text-slate-500 mt-1">
                  {child.badges.length === 0 ? "Encourage them to keep going!" : "Celebrate together 🎉"}
                </div>
              </div>
            </div>

            {/* Badges */}
            {child.badges.length > 0 && (
              <div className="bg-white rounded-3xl p-6 border border-slate-200" data-testid="parent-badges-card">
                <h3 className="font-outfit font-bold text-xl mb-4">Achievements</h3>
                <div className="flex flex-wrap gap-3">
                  {child.badges.map((b) => {
                    const Icon = ICONS[b.icon] || Sparkle;
                    return (
                      <div
                        key={b.id}
                        className={`rounded-2xl p-4 flex items-start gap-3 min-w-[200px] ${TONES[b.tone] || "bg-slate-100"}`}
                        data-testid={`parent-badge-${b.id}`}
                      >
                        <Icon weight="fill" size={22} />
                        <div>
                          <div className="font-outfit font-bold leading-tight">{b.title}</div>
                          <div className="text-xs opacity-80 mt-0.5">{b.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* This week */}
            {child.current_week && (
              <div className="bg-white rounded-3xl p-6 border border-slate-200" data-testid="parent-current-week">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold tracking-wider uppercase text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                    This week · Week {child.current_week.week_num}
                  </span>
                </div>
                <h3 className="font-outfit font-bold text-2xl mt-1">{child.current_week.title}</h3>
                <p className="font-caveat text-xl text-yellow-700">{child.current_week.tagline}</p>
                <ul className="mt-4 space-y-2">
                  {child.current_week.tasks.map((t, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      {t.verified ? (
                        <CheckCircle weight="fill" size={20} className="text-emerald-500 mt-0.5 shrink-0" />
                      ) : t.submitted ? (
                        <Clock weight="fill" size={20} className="text-amber-500 mt-0.5 shrink-0" />
                      ) : (
                        <Circle size={20} className="text-slate-300 mt-0.5 shrink-0" />
                      )}
                      <span className={t.verified ? "line-through text-slate-400" : "text-slate-800"}>{t.text}</span>
                      {t.submitted && !t.verified && (
                        <span className="ml-auto text-[10px] font-bold tracking-wider uppercase text-amber-700">Pending</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Conversation starters */}
            {child.current_week?.conversation_starters?.length > 0 && (
              <div className="bg-gradient-to-br from-pink-50 to-amber-50 rounded-3xl p-6 border border-amber-200" data-testid="parent-conversation-starters">
                <div className="flex items-center gap-2 mb-3">
                  <ChatCircleText weight="fill" className="text-pink-500" size={22} />
                  <h3 className="font-outfit font-bold text-xl">Ask {child.student.name} at dinner</h3>
                </div>
                <ul className="space-y-2">
                  {child.current_week.conversation_starters.map((q, idx) => (
                    <li
                      key={idx}
                      className="bg-white/80 rounded-xl px-4 py-3 text-slate-800 font-caveat text-xl"
                      data-testid={`parent-starter-${idx}`}
                    >
                      "{q}"
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Upcoming */}
            {child.upcoming.length > 0 && (
              <div className="bg-white rounded-3xl p-6 border border-slate-200" data-testid="parent-upcoming">
                <h3 className="font-outfit font-bold text-xl mb-1">Coming up</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Tap any week to peek at the lessons and activities. Preview only — tasks are marked complete in class.
                </p>
                <div className="space-y-2">
                  {child.upcoming.map((u) => (
                    <UpcomingWeekAccordion key={u.week_num} week={u} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <HighlightsCard readOnly />
      </main>
    </div>
  );
}
